/**
 * MessageRow — one user or agent turn in the project chat transcript.
 *
 * Ported from D9 with two things preserved verbatim because they are correctness, not styling:
 *
 *  • **The "imported — authorship unavailable" label.** Backfilled `artifact_chat_jobs` rows have no
 *    actor column and their ordering is a reconstruction, so presenting them as a faithful replay
 *    would be a lie. The expectation was explicitly reset in master plan §5.2 / Track 3 §5.3.3.
 *  • **The honest streaming fallback.** When no stage frame has arrived, the copy says files finish
 *    one at a time — it does not invent a stage. A fake stage that desynchronises is worse than a
 *    spinner (CR §3.2's language discipline).
 *
 * Per-file progress is rendered as a structured list rather than one spinner (Track 3 §5.2), because
 * a 3-file turn genuinely completes one branch at a time and that is the visible payoff of D8.
 *
 * ── LIVE REGIONS ───────────────────────────────────────────────────────────────────────────────
 * This row has no live region of its own. The transcript container is the single `role="log"
 * aria-live="polite"`, and nesting another live region inside it causes double announcement in
 * JAWS/NVDA. Per-file stage text is announced two ways instead: by the log's
 * `aria-relevant="additions text"`, and by `ChatTranscript`'s dedicated sr-only status summary
 * (which satisfies Track 3 T3-5 without nesting).
 */

import { Loader2, Lock } from "lucide-react";

import { cn } from "@/lib/utils";
import { ARTIFACT_LABELS, type ArtifactType } from "@/hooks/useArtifacts";
import {
  AGENT_STREAM_STAGE_LABELS,
  type ProjectChatMessage,
  type ProjectChatStreamState,
} from "@/hooks/useProjectChat";
import { ResultRow } from "./ResultRow";
import { describeAllSkippedTurn } from "./skipNotice";

export interface MessageRowProps {
  projectId: string;
  message: ProjectChatMessage;
  /** Live stage/narration for the in-flight turn, if this row is it (CR item E1). */
  stream?: ProjectChatStreamState;
  /** Forwarded to `ResultRow` so focus returns to the composer when a proposal is resolved. */
  onResultResolved?: () => void;
  /** Forwarded to `ResultRow` so a locked-file notice can offer to open that file. */
  onOpenArtifact?: (artifactType: ArtifactType) => void;
  /**
   * True when an agent turn exists for this user turn. Computed by `ChatTranscript`, which is the
   * only component that can see the next message.
   *
   * 🚨 This is load-bearing and NOT derivable from this row alone. Skip rows hang off the USER
   * message while proposals land on the AGENT message, so "every result on this row is a skip" is
   * also true of a MIXED turn (one file locked, one edited). Using that alone made a successful
   * partial turn claim "No changes were made" — caught by the mixed-turn test in
   * `ProjectChatView.lockedArtifact.test.tsx`.
   */
  agentReplyFollows?: boolean;
  /** Forwarded to `ResultRow` — whether the viewer may accept / reject proposals. */
  canResolve?: boolean;
}

export function MessageRow({
  projectId,
  message,
  stream,
  onResultResolved,
  onOpenArtifact,
  agentReplyFollows = false,
  canResolve = true,
}: MessageRowProps): JSX.Element {
  const isUser = message.role === "user";
  const isInFlight =
    message.status === "pending" || message.status === "processing";
  const isThisTurnStreaming = stream?.message_id === message.message_id;

  // ── THE "SILENT NO-OP" CASES ─────────────────────────────────────────────────────────────────
  // Both of these are turns where the backend deliberately creates NO agent row, so without an
  // explicit banner the transcript's only response to the instruction is the absence of one.
  //
  //  1. Every tagged file was locked by the pre-flight. Skip rows hang off the USER message (a skip
  //     is a fact about the instruction, not about an agent action), so this is detectable here:
  //     the user turn has results and none of them is a proposal.
  //  2. The message was untagged. `postTurn` writes no agent row and `post-project-chat-message.ts`
  //     returns 202 with `agent_message_id: null` — there is no question-answering path. The
  //     composer copy no longer invites this, but history and muscle memory still produce it.
  //
  // `imported` rows are excluded from case 2: backfilled per-artifact history is a reconstruction,
  // and annotating it with a rule that did not exist when it was written would be a lie.
  const allSkipped =
    isUser &&
    !agentReplyFollows &&
    message.results.length > 0 &&
    message.results.every((r) => r.resolution !== "proposed");
  const skippedSummary = allSkipped
    ? describeAllSkippedTurn(
        message.results.map(
          (r) => ARTIFACT_LABELS[r.artifact_type] ?? r.artifact_type,
        ),
      )
    : null;
  const isUnanswerableUntagged =
    isUser &&
    !message.imported &&
    !agentReplyFollows &&
    message.tagged_artifact_types.length === 0 &&
    message.results.length === 0;

  return (
    <div
      className={cn(
        "flex flex-col gap-1",
        isUser ? "items-end" : "items-start",
      )}
    >
      <div
        className={cn(
          "max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm",
          isUser
            ? "bg-primary text-white"
            : "border border-outlines bg-subtle-bg text-text-primary",
        )}
      >
        {message.body}

        {message.tagged_artifact_types.length > 0 && (
          <div
            className={cn(
              "mt-1.5 text-xs",
              isUser ? "text-white/85" : "text-text-secondary",
            )}
          >
            {/* On a user turn these are the files TAGGED; on an agent turn, the files TOUCHED. */}
            {isUser ? "Applies to: " : "Changed: "}
            {message.tagged_artifact_types
              .map((t) => ARTIFACT_LABELS[t] ?? t)
              .join(" · ")}
          </div>
        )}

        {message.imported && (
          <div
            className={cn(
              "mt-1 text-xs italic",
              isUser ? "text-white/75" : "text-text-secondary",
            )}
          >
            imported — authorship unavailable
          </div>
        )}
      </div>

      {message.status === "failed" && message.error_message && (
        <p className="max-w-[85%] text-xs text-error">
          {message.error_message}
        </p>
      )}

      {isInFlight && (
        <div className="flex max-w-[85%] flex-col gap-1 rounded-md border border-outlines bg-white px-3 py-2 text-xs text-text-secondary">
          <div className="flex items-center gap-2">
            <Loader2
              className="h-3 w-3 shrink-0 animate-spin"
              aria-hidden="true"
            />
            <span className="font-medium text-text-primary">
              {isThisTurnStreaming && stream.stage
                ? AGENT_STREAM_STAGE_LABELS[stream.stage]
                : "Working… files finish one at a time, so results appear as they land."}
            </span>
          </div>

          {isThisTurnStreaming && (
            <ul className="flex flex-col gap-0.5 pl-5">
              {Object.entries(stream.per_file ?? {}).map(
                ([artifactType, stage]) => (
                  <li key={artifactType}>
                    <span className="font-medium text-text-primary">
                      {ARTIFACT_LABELS[artifactType as ArtifactType] ??
                        artifactType}
                    </span>
                    {": "}
                    {AGENT_STREAM_STAGE_LABELS[stage]}
                    {stream.notes?.[artifactType as ArtifactType] ? (
                      // Narration streams in as it is generated. The DOCUMENT never arrives over the
                      // socket — it is persisted server-side and the diff renders from the result row
                      // once the turn completes (CR Decision 3 / §3.2).
                      <span className="ml-1 italic">
                        {stream.notes[artifactType as ArtifactType]}
                      </span>
                    ) : null}
                  </li>
                ),
              )}
            </ul>
          )}
        </div>
      )}

      <div className="w-full">
        {skippedSummary && (
          <div
            className="mt-2 flex items-start gap-2 rounded-md border border-warning/40 bg-badge-yellow-bg p-3 text-sm"
            role="note"
          >
            <Lock
              className="mt-0.5 h-4 w-4 shrink-0 text-warning"
              aria-hidden="true"
            />
            <div>
              <p className="font-medium text-text-primary">
                {skippedSummary.title}
              </p>
              <p className="mt-1 text-text-secondary">
                {skippedSummary.detail}
              </p>
            </div>
          </div>
        )}

        {isUnanswerableUntagged && (
          <div
            className="mt-2 rounded-md border border-outlines bg-subtle-bg p-3 text-sm"
            role="note"
          >
            <p className="font-medium text-text-primary">
              This message was not sent to the agent.
            </p>
            <p className="mt-1 text-text-secondary">
              Chat proposes edits to the files you tag. It cannot answer a
              message with no files tagged. Tag the files this applies to and
              send it again.
            </p>
          </div>
        )}

        {message.results.map((result) => (
          <ResultRow
            key={result.result_id}
            projectId={projectId}
            result={result}
            onResolved={onResultResolved}
            onOpenArtifact={onOpenArtifact}
            canResolve={canResolve}
          />
        ))}
      </div>
    </div>
  );
}

export default MessageRow;
